// The improved version of the My Intrivity Application
import { html, css, LitElement } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { clone, cloneDeep } from 'lodash-es';
import { storage, functions, auth } from './intrivity_app.js'
import { getStorage, ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { t, tHtmlString, getLocale, formatDate } from './i18n.js';


// EVENT CONSTANTS
export const INTRIVITY_GOOGLE_LOGGED_IN = "IntrivityLogin:Google";
export const INTRIVITY_GOOGLE_LOGGED_OUT = "IntrivityLoggedOut:Google";
export const INTRIVITY_LOGIN = "IntrivityLogin:Start";
export const INTRIVITY_LOGGED_IN = "IntrivityLogin:Success";
export const INTRIVITY_LOGIN_ERROR = "IntrivityLogin:Error";
export const INTRIVITY_LOGOUT = "IntrivityLogin:Stop";
export const INTRIVITY_NAV_SELECT = "IntrivityNav:Select";
export const INTRIVITY_PRINTABLE_TOGGLE = "IntrivityNav:Printable";
export const INTRIVITY_ADMIN = "IntrivityNav:Admin";
export const INTRIVITY_LOAD_GROUPS = "IntrivityGroups:Load";
export const INTRIVITY_LOAD_GROUP_FROM_STORAGE = "IntrivityGroupStorage:Load";
export const INTRIVITY_LOADED_GROUP = "IntrivityGroupStorage:Loaded";
export const INTRIVITY_LOAD_RESOURCES = "IntrivityResources:Load";
export const INTRIVITY_LOAD_TRAINER = "IntrivityTrainer:Load";
export const INTRIVITY_LOAD_PROGRAM_MANAGER = "IntrivityProgramManager:Load";
export const INTRIVITY_LOAD_CLIENT_MANAGER = "IntrivityClientManager:Load";
export const INTRIVITY_LOAD_CLIENTS = "IntrivityClients:Load";
export const INTRIVITY_GROUP_SELECT = "IntrivityGroup:Select";
export const INTRIVITY_GROUP_REPORTS_LOADED = "IntrivityGroups:Loaded";
export const INTRIVITY_MASQUERADE = "IntrivityLogin:Masquerade";
export const INTRIVITY_AI_SUMMARY = "IntrivityAI:Summary";
export const INTRIVITY_LOCALE_CHANGED = "IntrivityLocale:Changed";          // fired on window by i18n.js
export const INTRIVITY_STRIPE_PUBLIC_TEST_KEY = "pk_test_51SIubzCJ2OuklhonU3F9RAlZ0ifvFR6OBOcru9842iXnNKvdfHSnjMzGN2AB2b332prgj5pV40mqxZexFDhpKhOQ00JINrijtr";
export const INTRIVITY_STRIPE_PUBLIC_KEY = "pk_live_51OgbMhC2UaLrSQqg81spQbmy8AKR1tESH335ActIxRtY5PCwVudcsUGuaBRwxlqHxrotrTr6ncnMTOW0MaS1yw9y00zfxG0ela";

export const feedback_mapping = ['a','b','c','d','e','f','g'];

// Academic sources cited on the AI Summary Report. Keep in sync with the
// grounding excerpt baked into functions/ai.js's Gemini prompt.
export const AI_SUMMARY_SOURCES = [
  {
    citation: 'Adair, W. L., Buchan, N. R., Chen, X.-P., & Liu, L. A. (2024). What Isn\'t Being Said: Culture and Communication at Work. Springer.',
    url: 'https://doi.org/10.1007/978-3-031-53645-8',
  },
];

export const escapeHtml = function(str) {
    return String(str ?? '').replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
};

export const aiDomainColor = function(code) {
    return { m: 'rgb(250,166,53)', r: 'rgb(82,162,170)', t: 'rgb(217,90,123)', s: 'rgb(175,208,117)' }[code] || '#999';
};

// Inline HTML/SVG for one MRTS badge "marker" cell, mirroring
// IntrivityBadgeMarker's shapes (badge.js) -- the real custom element can't
// render into the serialized HTML used by the print window / backend PDF
// renderer, so this reproduces the same 1-5 shapes as plain markup.
function _badgeMarkerHtml(value, color, px) {
    const open = `<svg width="${px}" height="${px}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" stroke="${color}" fill="transparent" stroke-width="1"/>`;
    if (value === 2) return `${open}<polygon points="${px/2},${px} ${px},${px/2} ${px},${px}" fill="${color}"/></svg>`;
    if (value === 3) return `${open}<polygon points="${px},0 ${px},${px} 0,${px}" fill="${color}"/></svg>`;
    if (value === 4) return `${open}<polygon points="0,${px/2} 0,${px} ${px},${px} ${px},0 ${px/2},0" fill="${color}"/></svg>`;
    if (value === 5) return `<svg width="${px}" height="${px}" xmlns="http://www.w3.org/2000/svg"><rect width="100%" height="100%" stroke="${color}" fill="${color}" stroke-width="1"/></svg>`;
    return `${open}</svg>`;
}

// Inline HTML for a small MRTS badge (2x2 marker grid), for the print window
// / backend PDF renderer -- see _badgeMarkerHtml above.
export const buildBadgeHtml = function(m, r, s, t, px = 12) {
    const cell = (value, color) => `<span style="display:inline-block; width:${px}px; height:${px}px;">${_badgeMarkerHtml(value, color, px)}</span>`;
    return `<span style="display:inline-grid; grid-template-columns:${px}px ${px}px; grid-gap:2px; vertical-align:middle;">`
        + cell(m, aiDomainColor('m')) + cell(r, aiDomainColor('r')) + cell(s, aiDomainColor('s')) + cell(t, aiDomainColor('t'))
        + `</span>`;
};

// mwc-select toggle fields (ai_enabled, group_enabled, disable_report_privacy,
// etc.) round-trip through a DOM attribute, so an "off" selection can be
// saved as the string '0' — which is truthy in JS. Treat only 1/'1'/true as on.
export const isFlagOn = function(value) {
    return value === 1 || value === '1' || value === true;
};

// First and last name for an invitation. A profile only has firstName/
// lastName when the sign-in provider supplied given and family names;
// otherwise split its display name at the last word, so a middle name stays
// with the first name. Mirrors participantName() in functions/groups.js.
export const invitationName = function(user) {
    let first = user.firstName ? String(user.firstName).trim() : '';
    let last = user.lastName ? String(user.lastName).trim() : '';
    if (!first && !last) {
        const full = String(user.fullName || user.displayName || '').trim();
        if (full) {
            const parts = full.split(/\s+/);
            last = parts.length > 1 ? parts.pop() : '';
            first = parts.join(' ');
        }
    }
    return { first, last };
};

// AI features (Generate AI Summary buttons, bulk generation) default to
// disabled and require every level of the client/group/team chain to opt in
// — turning it off anywhere in the chain turns it off for everything below.
// `parentGroup` only applies to a team (its parent top-level group); omit it
// for a top-level group. Mirrored server-side in functions/ai.js.
export const cascadeAiEnabled = function(client, group, parentGroup) {
    if (!client || !isFlagOn(client.ai_enabled)) return false;
    if (!group || !isFlagOn(group.ai_enabled)) return false;
    if (parentGroup && !isFlagOn(parentGroup.ai_enabled)) return false;
    return true;
};

// Shared <style> for the standalone AI Summary Report print document(s).
// Used both for a single user's report (name_banner_app.js) and a
// combined multi-user document (intrivity-admin-groups.js's bulk
// generation) — .ai-summary-section+.ai-summary-section forces a page
// break between each user's section in the combined case.
export const AI_SUMMARY_PRINT_STYLE = `
    /* Sized so the report already fits well at a browser's default 100%
       print scale — most rules below use em units, which cascade off this
       base size. Browsers don't expose an API to preset the print dialog's
       own Scale control, so if this still isn't right on someone's
       particular browser/printer, that control is still there for them to
       adjust further, same as before. */
    body { font-family: 'Muli', sans-serif; color: #222; margin: 0; padding: 0.4in; font-size: 9pt; }
    h1 { font-size: 1.6em; margin: 0 0 4px 0; }
    h2 { font-size: 1.3em; margin: 0 0 4px 0; }
    .team { color: #666; margin-bottom: 10px; }
    .team-members { display: flex; flex-wrap: wrap; gap: 6px 14px; margin: 6px 0 10px 0; }
    .team-member { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; font-size: 1em; }
    .ai-summary-section + .ai-summary-section { page-break-before: always; }
    table { width: 100%; border-collapse: collapse; font-size: 1.05em; }
    th, td { border: 1px solid #ddd; padding: 5px 6px; text-align: left; vertical-align: top; }
    th { color: #1a1a1a; font-weight: 600; }   /* white on the domain colours fails AA */
    td:first-child { font-weight: 600; white-space: nowrap; background: #f5f5f5; width: 60px; }
    .score-line { font-size: 0.9em; color: #1a1a1a; font-weight: normal; margin-top: 3px; }
    .item-title { font-weight: 600; margin-bottom: 3px; }
    .item-desc { margin-bottom: 5px; line-height: 1.45; }
    .team-dynamics { margin-top: 10px; padding: 8px 11px; background: #e3e3e3; border-top: 3px solid rgb(250,166,53); line-height: 1.55; }
    .generated-at { font-size: 0.85em; color: #6b6b6b; margin-top: 7px; text-align: right; }
    .sources { margin-top: 8px; padding-top: 5px; border-top: 1px solid #eee; font-size: 0.82em; color: #6b6b6b; line-height: 1.5; }
    .sources a { color: #6b6b6b; }
    .disclaimer { margin-top: 8px; padding-top: 5px; border-top: 1px solid #eee; font-size: 0.78em; color: #6b6b6b; line-height: 1.5; font-style: italic; }
    thead { display: table-header-group; }
    tr, td, th, .team-dynamics, .generated-at, .sources, .disclaimer { break-inside: avoid; }
    @page { margin: 0.4in; }
    @media print {
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
`;

// Opens a popup, writes a standalone HTML document into it, and triggers
// the browser print dialog. `window.onload` alone isn't enough of a signal
// here: it can fire before the popup has finished a real layout/paint pass
// (especially right after document.write()) and before the Google Fonts
// <link> has actually applied, so window.print() ends up capturing a blank
// or half-styled page the first time -- cancelling and reopening print
// manually works because the document has since finished settling. Waiting
// on document.fonts.ready plus a couple of animation frames ensures a full
// render has actually happened before we ask for the print preview.
export const openPrintWindow = function(html, { onBlocked } = {}) {
    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    if (!printWindow) {
        if (onBlocked) onBlocked();
        return null;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = async () => {
        try {
            if (printWindow.document.fonts && printWindow.document.fonts.ready) {
                await printWindow.document.fonts.ready;
            }
        } catch (e) {
            // Best effort -- fall through to print anyway.
        }
        printWindow.requestAnimationFrame(() => {
            printWindow.requestAnimationFrame(() => {
                printWindow.focus();
                printWindow.print();
            });
        });
    };
    return printWindow;
};

// Per-client toggle between the two ways an AI Summary report can become a
// PDF: PDF_MODE_BROWSER (default) opens a print window via openPrintWindow()
// above and lets the browser's own print-to-PDF handle it; PDF_MODE_BACKEND
// renders it server-side (see functions/pdf.js) for a consistent,
// browser-independent result. Unset/anything else on the client doc falls
// back to PDF_MODE_BROWSER.
export const PDF_MODE_BROWSER = 'browser';
export const PDF_MODE_BACKEND = 'backend';

export const resolvePdfMode = function(client) {
    return (client && client.pdf_mode === PDF_MODE_BACKEND) ? PDF_MODE_BACKEND : PDF_MODE_BROWSER;
};

// Opens a blank tab synchronously (inside the caller's click handler, so it
// still carries the click's transient user activation) with a placeholder
// message, for a caller that will redirect it once a slower async result is
// ready. Calling window.open() only after that async call resolves risks
// the browser's popup blocker silently swallowing it -- Chromium's user
// activation from a click lasts only a few seconds, which server-side PDF/
// ZIP rendering (especially a multi-person ZIP, one Puppeteer render per
// person) can easily outlast. Returns null if even this immediate open was
// blocked.
function openLoadingWindow(message) {
    const win = window.open('', '_blank');
    if (win) {
        win.document.write(`<!doctype html><html lang="${getLocale()}"><title>${message}</title>` +
            `<body style="font-family:sans-serif;padding:2em;color:#444;">${message}</body>`);
    }
    return win;
}

// Calls the generateSummaryPdf callable (see functions/pdf.js) and opens the
// resulting signed Storage URL in a new tab. `sections` is
// [{ summary, heading, contextTitle, parentGroupTitle }, ...] -- the same
// shape buildAiSummarySectionHtml takes, since the backend builds the HTML
// itself from these pieces rather than accepting raw HTML from the client.
export const generateAndOpenSummaryPdf = async function({ documentTitle, sections }) {
    const opened = openLoadingWindow(escapeHtml(t('pdf.preparingPdf')));
    const fn = httpsCallable(functions, 'generateSummaryPdf');
    let result;
    try {
        result = await fn({ documentTitle, sections });
    } catch (e) {
        if (opened) opened.close();
        throw e;
    }
    if (opened) {
        opened.location.href = result.data.pdfUrl;
    } else {
        window.open(result.data.pdfUrl, '_blank');
    }
};

// Calls the generateSummaryPdfZip callable (see functions/pdf.js) and opens
// the resulting signed Storage URL: a ZIP containing one PDF per person,
// each named after that person's email address. `entries` is
// [{ email, sections }, ...] -- each entry's `sections` in the same shape
// generateAndOpenSummaryPdf takes, scoped to that one person's own report.
// Only meaningful where generateAndOpenSummaryPdf (server-rendered PDFs) is
// already in use -- there's no print-dialog equivalent of a ZIP download.
export const generateAndDownloadSummaryZip = async function({ documentTitle, entries }) {
    const opened = openLoadingWindow(escapeHtml(t('pdf.preparingZip')));
    const fn = httpsCallable(functions, 'generateSummaryPdfZip');
    let result;
    try {
        result = await fn({ documentTitle, entries });
    } catch (e) {
        if (opened) opened.close();
        throw e;
    }
    const target = opened || window.open(result.data.zipUrl, '_blank');
    if (opened) {
        opened.location.href = result.data.zipUrl;
    }
    // A ZIP's Content-Disposition is "attachment", so navigating to it never
    // actually replaces this placeholder page the way the inline PDF does --
    // the browser just starts the download in the background and leaves the
    // tab sitting on "Preparing your ZIP...". Close it once the download has
    // had a moment to register (closing immediately can cancel a download
    // that hasn't started yet in some browsers).
    if (target) {
        setTimeout(() => {
            try { target.close(); } catch (e) { /* already closed */ }
        }, 1500);
    }
};

// Builds the inner HTML for one user's AI Summary section: heading, domain
// table, team dynamics, sources, disclaimer, and generated timestamp.
// `heading` is shown as an <h1> (pass the student's name alone for a
// section inside a combined multi-user document). `members` (an array of
// { name, m, r, s, t }) is only meaningful for a team-level report -- pass
// it whenever `parentGroupTitle` is set to list each team member with their
// MRTS badge under the "Team-Level Report" label.
//
// `parentGroupTitle` is what distinguishes the two kinds of cohort report:
// present means a team inside a group ("Team-Level Report"), absent means the
// group itself ("Group-Level Report"). Both are labelled, so a reader never
// has to infer which one they are holding from the title alone.
// When an AI summary was generated, in the reader's language. Always Eastern
// time, since that is what support quotes back when someone reports a problem.
export const formatAiGeneratedAt = function(value) {
    return formatDate(value, {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        timeZone: 'America/New_York', timeZoneName: 'short',
    });
};

export const buildAiSummarySectionHtml = function(s, { heading, contextTitle, parentGroupTitle, members } = {}) {
    const esc = escapeHtml;

    const domainHeaders = s.domains.map(d => `
        <th style="background:${aiDomainColor(d.code)}">
            ${esc(d.name)}
            <div class="score-line">${esc(t('ai.scoreLine', { you: d.user_score, team: d.group_score }))}</div>
        </th>`).join('');

    const rowCells = (key) => s.domains.map(d => `
        <td>
            ${d[key].map(item => `
                <div class="item-title">${esc(item.title)}</div>
                <div class="item-desc">${esc(item.description)}</div>
            `).join('')}
        </td>`).join('');

    const sourcesHtml = AI_SUMMARY_SOURCES.map(src => `
        <div>${esc(src.citation)}${src.url ? ` <a href="${esc(src.url)}">${esc(src.url)}</a>` : ''}</div>
    `).join('');

    const membersHtml = (members && members.length) ? `
        <div class="team-members">
            ${members.map(mem => `
                <span class="team-member">${buildBadgeHtml(mem.m, mem.r, mem.s, mem.t)} ${esc(mem.name)}</span>
            `).join('')}
        </div>` : '';

    return `
    <div class="ai-summary-section">
    ${heading ? `<h1>${esc(heading)}</h1>` : ''}
    ${parentGroupTitle
        ? `<div class="team">${tHtmlString('ai.teamLevel', { title: contextTitle })}</div>${membersHtml}`
        : (contextTitle ? `<div class="team">${tHtmlString('ai.groupLevel', { title: contextTitle })}</div>` : '')}
    <table>
        <thead><tr><th></th>${domainHeaders}</tr></thead>
        <tbody>
            <tr><td>${esc(t('ai.strengths'))}</td>${rowCells('strengths')}</tr>
            <tr><td>${esc(t('ai.opportunities'))}</td>${rowCells('opportunities')}</tr>
        </tbody>
    </table>
    ${s.team_dynamics ? `<div class="team-dynamics"><strong>${esc(t('ai.teamDynamics'))}</strong> ${esc(s.team_dynamics)}</div>` : ''}
    <div class="sources">${esc(t('ai.sources'))} ${sourcesHtml}</div>
    <div class="disclaimer">${esc(t('ai.disclaimer'))}</div>
    ${s.generated_at ? `<div class="generated-at">${esc(t('ai.generatedAt', { date: formatAiGeneratedAt(s.generated_at) }))}</div>` : ''}
    </div>`;
};

// STATIC FUNCTIONS
// log event
export const debugLog = function(remote, ob) {
    console.log(ob); 
};
// emit an event
export const emitEvent = function (eventName, eventDetails, element) {
    let event = new CustomEvent(
            eventName, {'bubbles': true, 'composed': true, 'detail': eventDetails});
    element.dispatchEvent(event);
    console.log(event);
};
// Determine if the user has a give role
export const hasRole = function(role, user) {
    return user && user.role && user.role.includes(role);
};
// Determine if the "name" is the key of a value in local storage
export const hasLocalStorage = function(name) {
    try {
        let val = localStorage.getItem(name);
        if (val) {
            return true;
        } else {
            return false;
        }
    } catch (e) {
        console.log(e);
        return false;
    }
};  
// Based on the row number return the background color CSS of white or gray
export const backgroundRow = function(i) {
        return i % 2 == 0 ? "background-color: #eee;" : "background-color: white;";
};
// Get the net score form a raw score
export const getScore = function(raw) {
        if (raw < 1.9) {
            return 1;
        } else if (raw < 2.7) {
            return 2;
        } else if (raw < 3.5) {
            return 3;
        } else if (raw < 4.3) {
            return 4;
        } else {
            return 5;
        }        
};
// If item equals the net score return the color or disabled color
export const getColor = function(item, raw, color) {
        return item == getScore(raw) ? color: 'lightgrey';
};
// Based on values from 1 to 5, generate an mrts icon as appropriate  
// `label` makes the shape a labelled image; without one it is treated as
// decorative, because in most places the numeric score is already rendered as
// text right next to it (WCAG 1.1.1).
export const getIcon = function(item, raw, color, size, label) {

        if (item == 1) {
            return html`
                <svg role=${ifDefined(label ? 'img' : undefined)} aria-label=${ifDefined(label)} aria-hidden=${ifDefined(label ? undefined : 'true')} focusable="false" style="aspect-ratio: 1; width: ${size ? size: '25'}px;" xmlns="http://www.w3.org/2000/svg" version="1.1">
                    <rect width="100%" height="100%" stroke="${getColor(1, raw, color)}" fill="transparent" stroke-width="3" />
                </svg>`;
        } else if (item == 2) {
            return html`
                <svg role=${ifDefined(label ? 'img' : undefined)} aria-label=${ifDefined(label)} aria-hidden=${ifDefined(label ? undefined : 'true')} focusable="false" style="aspect-ratio: 1; width: ${size ? size: '25'}px;" xmlns="http://www.w3.org/2000/svg" version="1.1">
                    <rect width="100%" height="100%" stroke="${getColor(2, raw, color)}" fill="transparent" stroke-width="1" />
                    <polygon points="${size ? size/2: 25/2},${size ? size: '25'} ${size ? size: '25'},${size ? size/2: 25/2} ${size ? size: '25'},${size ? size: '25'}" fill="${getColor(2, raw, color)}"/>
                </svg>
            `;
        } else if (item == 3) {
            return html`
                <svg role=${ifDefined(label ? 'img' : undefined)} aria-label=${ifDefined(label)} aria-hidden=${ifDefined(label ? undefined : 'true')} focusable="false" style="aspect-ratio: 1; width: ${size ? size: '25'}px;" xmlns="http://www.w3.org/2000/svg" version="1.1">
                  <rect width="100%" height="100%" stroke="${getColor(3, raw, color)}" fill="transparent" stroke-width="1" />
                  <polygon points="${size ? size: '25'},0 ${size ? size: '25'},${size ? size: '25'} 0,${size ? size: '25'}" fill="${getColor(3, raw, color)}"/>
                </svg>
            `;
        } else if (item == 4) {
            return html`
                <svg role=${ifDefined(label ? 'img' : undefined)} aria-label=${ifDefined(label)} aria-hidden=${ifDefined(label ? undefined : 'true')} focusable="false" style="aspect-ratio: 1; width: ${size ? size: '25'}px;" xmlns="http://www.w3.org/2000/svg" version="1.1">
                    <rect width="100%" height="100%" stroke="${getColor(4, raw, color)}" fill="transparent" stroke-width="1" />
                    <polygon points="0,${size ? size/2: 12} 0,${size ? size: '25'} ${size ? size: '25'},${size ? size: '25'} ${size ? size: '25'},0 ${size ? size/2: 12},0" fill="${getColor(4, raw, color)}"/>
                </svg>    
            `;
        } else if (item == 5) {
            return html`
                <div role=${ifDefined(label ? 'img' : undefined)} aria-label=${ifDefined(label)} aria-hidden=${ifDefined(label ? undefined : 'true')} style="align: center; background-color: ${getColor(5, raw, color)}; aspect-ratio: 1; width: ${size ? size: '25'}px;"></div>              
            `;
        }
};
export const putJSONInStorage = async function(name, json) {
    var data = JSON.stringify(json);
    const blob = new Blob([data], { type: "application/json" });
    const storageRef = ref(storage, name);
    uploadBytes(storageRef, blob).then((snapshot) => {
        console.log('Put in cloud storage ' + JSON.stringify(json));
    });      
};
// Promise-based JSON fetch from Storage, with no localStorage caching —
// used for bulk operations that read many different users'/groups' files
// at once (e.g. bulk AI Summary generation), where per-file localStorage
// caching would be wrong/wasteful. Resolves to null if the file doesn't
// exist or can't be read.
export const fetchStorageJson = async function(file_name) {
    try {
        const storageRef = ref(storage, file_name);
        const url = await getDownloadURL(storageRef);
        const response = await fetch(url);
        if (!response.ok) return null;
        return await response.json();
    } catch (e) {
        return null;
    }
};

export const loadJSONFromStorage = function(elem, var_name, file_name) {
    console.log('Loading from cloud storage: ' + var_name);
    const storageRef = ref(storage, file_name);
    getDownloadURL(storageRef)
        .then(url => fetch(url))
        .then(response => response.json())
        .then(data => { 
            elem[var_name] = data; 
            let store_this = cloneDeep(data);
            store_this.stored = new Date().toISOString();
            localStorage.setItem(var_name, JSON.stringify(store_this)); 
        });
};
export const getJSONFromStorage = function(elem, var_name, file_name, masqueraded_user) {
    try {
        let val = localStorage.getItem(var_name);
        if (val && !masqueraded_user) {
            console.log('From local storage: ' + var_name);

            elem[var_name] = JSON.parse(val);

            // check if last update version is > 1 day old
            if (elem[var_name].stored) {
                let now = new Date();
                let stored = new Date(elem[var_name].stored);
                let dayAgo = new Date(now.setDate(now.getDate() - 1));
                if (dayAgo > stored) { 
                    console.log('Stored more than 1 day ago: ' + var_name);
                    loadJSONFromStorage(elem, var_name, file_name); 
                }
            } else {
                console.log('No stored value for: ' + var_name);
                loadJSONFromStorage(elem, var_name, file_name);
            }

        } else {
            loadJSONFromStorage(elem, var_name, file_name);
        }
    } catch (e) {
        console.log(e);
    }
};
const _LETTERS = ['a', 'b', 'c', 'd', 'e'];

function _avgToLetter(avg) {
    const rounded = Math.max(1, Math.min(5, Math.round(avg)));
    return _LETTERS[rounded - 1];
}

function _pickAE(obj, letter) {
    if (obj && typeof obj === 'object' && 'a' in obj && 'e' in obj) {
        return obj[letter];
    }
    return obj;
}

function _buildBehavior(domBehavior, userBehavior) {
    const letter = _avgToLetter(userBehavior.average);
    const out = {
        name: domBehavior.name,
        code: domBehavior.code,
        score: { count: userBehavior.count, sum: userBehavior.sum, average: userBehavior.average, ...(userBehavior.responses && { responses: userBehavior.responses }) },
        letter,
    };
    if (domBehavior.quote)       out.quote       = domBehavior.quote;
    if (domBehavior.definition)  out.definition  = domBehavior.definition;
    if (domBehavior.sample)      out.sample      = domBehavior.sample;
    if (domBehavior.left_label)  out.left_label  = domBehavior.left_label;
    if (domBehavior.right_label) out.right_label = domBehavior.right_label;
    if (domBehavior.meaning)         out.meaning         = _pickAE(domBehavior.meaning, letter);
    if (domBehavior.strengths)       out.strengths       = _pickAE(domBehavior.strengths, letter);
    if (domBehavior.recommendations) out.recommendations = _pickAE(domBehavior.recommendations, letter);
    return out;
}

function _buildAttitude(domAttitude, userAttitude) {
    const letter = _avgToLetter(userAttitude.average);
    const out = {
        name: domAttitude.name,
        code: domAttitude.code,
        score: { count: userAttitude.count, sum: userAttitude.sum, average: userAttitude.average, ...(userAttitude.responses && { responses: userAttitude.responses }) },
        letter,
    };
    if (domAttitude.quote)       out.quote       = domAttitude.quote;
    if (domAttitude.definition)  out.definition  = domAttitude.definition;
    if (domAttitude.sample)      out.sample      = domAttitude.sample;
    if (domAttitude.left_label)  out.left_label  = domAttitude.left_label;
    if (domAttitude.right_label) out.right_label = domAttitude.right_label;
    if (domAttitude.meaning)         out.meaning         = _pickAE(domAttitude.meaning, letter);
    if (domAttitude.strengths)       out.strengths       = _pickAE(domAttitude.strengths, letter);
    if (domAttitude.recommendations) out.recommendations = _pickAE(domAttitude.recommendations, letter);
    return out;
}

function _buildLevelTwo(domL2, userL2) {
    const letter = _avgToLetter(userL2.average);
    const out = {
        name: domL2.name,
        code: domL2.code,
        score: { sum: userL2.sum, average: userL2.average, ...(userL2.responses && { responses: userL2.responses }) },
        letter,
    };
    if (domL2.description) out.description = domL2.description;
    if (domL2.left_label)  out.left_label  = domL2.left_label;
    if (domL2.right_label) out.right_label = domL2.right_label;
    if (domL2.feedback)    out.feedback    = _pickAE(domL2.feedback, letter);
    out.behaviors = userL2.behaviors.flatMap(userBehavior => {
        const domBehavior = domL2.behaviors.find(b => b.code === userBehavior.code);
        return domBehavior ? [_buildBehavior(domBehavior, userBehavior)] : [];
    });
    return out;
}

function _buildDomain(domainFile, userDomain) {
    const letter = _avgToLetter(userDomain.average);
    const out = {
        name: domainFile.name,
        code: domainFile.code,
        description: domainFile.description,
        color: domainFile.color,
        score: { sum: userDomain.sum, average: userDomain.average },
        letter,
        label: domainFile.label[_LETTERS.indexOf(letter)],
        feedback: _pickAE(domainFile.feedback, letter),
        attitude_description: domainFile.attitude_description,
    };
    out.attitudes = userDomain.attitudes.flatMap(userAtt => {
        const domAtt = domainFile.attitudes.find(a => a.code === userAtt.code);
        return domAtt ? [_buildAttitude(domAtt, userAtt)] : [];
    });
    out.level_two = userDomain.level_two.flatMap(userL2 => {
        const domL2 = domainFile.level_two.find(l => l.code === userL2.code);
        return domL2 ? [_buildLevelTwo(domL2, userL2)] : [];
    });
    return out;
}

// Merge a raw user report with domain interpretation files into a single
// combined report where every a–e text property is resolved to a single
// value based on the rounded average score for that element.
//
// userReport  – parsed user report JSON (keys: m, r, t, s, dem)
// domainFiles – { m, r, t, s } map of parsed domain JSON objects
export const buildCombinedReport = function(userReport, domainFiles) {
    return {
        dem: userReport.dem,
        domains: Object.keys(domainFiles).map(code => _buildDomain(domainFiles[code], userReport[code])),
    };
};

// Same as buildCombinedReport but for a group report. The aggregate average
// across all users is used to select interpretation text. Per-user responses
// are preserved in each score object.
//
// groupReport – parsed group report JSON (keys: m, r, t, s, group, title, users, num, attitudes)
// domainFiles – { m, r, t, s } map of parsed domain JSON objects
export const buildCombinedGroupReport = function(groupReport, domainFiles) {
    return {
        group: groupReport.group,
        title: groupReport.title,
        users: groupReport.users,
        num: groupReport.num,
        attitudes: groupReport.attitudes,
        domains: Object.keys(domainFiles).map(code => _buildDomain(domainFiles[code], groupReport[code])),
    };
};

// Each team member's real name and MRTS badge scores (1-5), sorted by name,
// for a team-level AI Summary report's member list. Names here are always
// real -- the group/team's report-privacy setting only anonymizes the AI
// Summary's own generated content (see functions/ai.js's anonymizeGroupUsers),
// not this membership list.
//
// users – a group/team report's `users` array (keys: firstName, lastName,
// profile: {m, r, t, s})
export const buildTeamMembers = function(users) {
    if (!Array.isArray(users)) return [];
    return [...users]
        // A member missing profile scores (report generation never completed
        // for them, e.g. a stale survey_id) shouldn't crash the roster for
        // everyone else sharing this report -- leave them out instead.
        .filter(u => u.profile)
        .sort((a, b) => (a.lastName + ', ' + a.firstName).localeCompare(b.lastName + ', ' + b.firstName))
        .map(u => ({
            name: `${u.lastName}, ${u.firstName}`,
            m: getScore(u.profile.m),
            r: getScore(u.profile.r),
            s: getScore(u.profile.s),
            t: getScore(u.profile.t),
        }));
};

// Display name for a group/team member's report row. The viewer always sees
// their own real name (matched by email — report data always retains real
// identity so the viewer can find their own responses); other members are
// shown as "Team Member N" unless the group/team's live disable_report_privacy
// setting is true. Callers should read that setting fresh from the group's
// Firestore doc rather than from cached report JSON, since report JSON is a
// point-in-time snapshot that won't reflect a later toggle.
export const groupMemberDisplayName = function(user, index, disableReportPrivacy, viewerEmail) {
    const isSelf = viewerEmail && user.email === viewerEmail;
    if (isSelf || disableReportPrivacy) {
        return `${user.lastName}, ${user.firstName}`;
    }
    return t('common.teamMember', { number: index + 1 });
};

export const loadGroupFromStorage = async function(groups, group, failover) {
    try {
        const storageRef = ref(storage, "reports/groups/" + group + ".json");
        getDownloadURL(storageRef)
            .then(url => fetch(url))
            .then(response => response.json())
            .then(data => { 
                groups.push(data); 
                let store_this = cloneDeep(data);
                store_this.stored = new Date().toISOString();
                localStorage.setItem("reports_" + group, JSON.stringify(store_this));
            });
    } catch (x) {
        console.log(x);
        if (failover) groups.push(failover);
        return group;
    }
};
// Public contact address shown on the accessibility and data privacy pages.
//
// Defined once because it is going to change: the address is currently on
// icedge.com, the former brand, and moves to intrivity.com later. Three
// hardcoded copies would mean three chances to miss one, and a stale contact
// address on a published accessibility statement is worse than no statement --
// it is a commitment nobody can act on.
export const CONTACT_EMAIL = 'support@icedge.com';

// Calls one of the superadmin-only onRequest maintenance endpoints
// (functions/admin_auth.js's guard()), e.g. inventoryuserdata/deleteuserdata
// in functions/retention.js. These are deliberately onRequest rather than
// onCall (see admin_auth.js's header comment), so the caller must attach a
// Firebase ID token by hand rather than relying on httpsCallable to do it --
// this is that one shared spot for doing so, instead of every call site
// re-deriving the emulator/production URL and the auth header.
//
// name: the exported function name, e.g. 'deleteuserdata'.
// params: query-string params, e.g. { email, confirm: 'ERASE', confirm_email }.
export async function callSuperadminEndpoint(name, params = {}) {
    const isEmulator = ['localhost', '127.0.0.1', 'localhost.fuqua.duke.edu']
        .includes(window.location.hostname);
    const base = isEmulator
        ? 'http://localhost:5001/intrivity-12c92/us-central1'
        : 'https://us-central1-intrivity-12c92.cloudfunctions.net';

    if (!auth.currentUser) {
        throw new Error(t('common.notSignedIn'));
    }
    const token = await auth.currentUser.getIdToken();

    const query = new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    ).toString();
    const url = `${base}/${name}${query ? `?${query}` : ''}`;

    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(body.error || t('common.endpointFailed', { name, status: res.status }));
    }
    return body;
}
